import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import BASE_URL from "../api";
import { AppDispatch, RootState } from "../../../store/store";
import {
  BookingState,
  PropertyBooking,
  CreateBookingPayload,
  UpdateBookingPayload,
} from "../types/booking";

/* =======================
   INITIAL STATE
======================= */

const initialState: BookingState = {
  bookings: [],
  myBookings: [],
  myPropertyBookings: [],
  booking: null,
  loading: false,
  error: null,
  success: null,
};

/* =======================
   API HELPER
======================= */

async function api<T>(
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  token?: string,
  body?: unknown
): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json();

  if (!res.ok) {
    const message =
      data?.message ||
      Object.values(data?.errors || {})?.flat()?.[0] ||
      "Request failed";

    throw new Error(message);
  }

  return data as T;
}

const getAuthToken = (state: RootState, role: "user" | "admin") => {
  return role === "admin"
    ? state.admin.access_token
    : state.auth.access_token;
};

/* =======================
   SLICE
======================= */

const bookingSlice = createSlice({
  name: "booking",
  initialState,
  reducers: {
    start(state) {
      state.loading = true;
      state.error = null;
      state.success = null;
    },

    fail(state, action: PayloadAction<any>) {
      state.loading = false;
      state.error = action.payload?.message || action.payload;
    },

    setSuccess(state, action: PayloadAction<string>) {
      state.loading = false;
      state.success = action.payload;
    },

    setBookings(state, action: PayloadAction<PropertyBooking[]>) {
      state.loading = false;
      state.bookings = action.payload || [];
    },

    setMyBookings(state, action: PayloadAction<PropertyBooking[]>) {
      state.loading = false;
      state.myBookings = action.payload || [];
    },

    setBooking(state, action: PayloadAction<PropertyBooking>) {
      state.loading = false;
      state.booking = action.payload;
    },

    setMyPropertyBookings(state, action: PayloadAction<PropertyBooking[]>) {
      state.loading = false;
      state.myPropertyBookings = action.payload || [];
    },


    addBooking(state, action: PayloadAction<PropertyBooking>) {
      state.loading = false;
      state.bookings.unshift(action.payload);
      state.myBookings.unshift(action.payload);
    },

    updateBooking(
      state,
      action: PayloadAction<PropertyBooking>
    ) {
      state.loading = false;

      const updateList = (
        list: PropertyBooking[]
      ) =>
        list.map((b) =>
          b.id === action.payload.id
            ? {
              ...b,
              ...action.payload,
            }
            : b
        );

      state.bookings = updateList(
        state.bookings
      );

      state.myBookings = updateList(
        state.myBookings
      );

      state.myPropertyBookings =
        updateList(
          state.myPropertyBookings
        );

      // OPTIONAL
      if (
        state.booking?.id ===
        action.payload.id
      ) {
        state.booking = {
          ...state.booking,
          ...action.payload,
        };
      }
    },

    deleteBooking(state, action: PayloadAction<string>) {
      state.loading = false;

      state.bookings = state.bookings.filter(b => b.id !== action.payload);
      state.myBookings = state.myBookings.filter(b => b.id !== action.payload);
    },
  },
});

export const {
  start,
  fail,
  setSuccess,
  setBookings,
  setMyBookings,
  setBooking,
  setMyPropertyBookings,
  addBooking,
  updateBooking,
  deleteBooking,
} = bookingSlice.actions;

export default bookingSlice.reducer;

/* =======================
   THUNKS
======================= */

/**
 * 📌 CREATE BOOKING
 */
export const createBooking =
  (body: CreateBookingPayload) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getState().auth.access_token;

        if (!token) throw new Error("Unauthorized");

        const data = await api<{ data: PropertyBooking; message: string }>(
          "/user/booking",
          "POST",
          token,
          body
        );

        dispatch(addBooking(data.data));
        dispatch(setSuccess(data.message));

        //  sync both lists
        dispatch(fetchBookings());
        dispatch(fetchMyBookings());

        return data;
      } catch (err: any) {
        // console.log("BOOKING ERROR =>", err); //  ADD THIS
        dispatch(fail(err));
        throw err;
      }
    };

/**
 * 📌 GET ALL BOOKINGS (ADMIN)
 */
export const fetchBookings =
  () =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAuthToken(getState(), "admin");

        if (!token) throw new Error("Unauthorized (admin)");

        const data = await api<{ data: PropertyBooking[] }>(
          "/admin/bookings",
          "GET",
          token
        );

        dispatch(setBookings(data.data));
      } catch (err: any) {
        dispatch(fail(err.message));
      }
    };

/**
 * 📌 MY BOOKINGS
 */


export const fetchMyBookings =
  () =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        //const token = getState().auth.access_token;
        const token = getAuthToken(getState(), "user");

        if (!token) throw new Error("Unauthorized");

        const data = await api<{ data: PropertyBooking[] }>(
          "/user/my-bookings",
          "GET",
          token
        );

        dispatch(setMyBookings(data.data));
      } catch (err: any) {
        dispatch(fail(err));
      }
    };

export const fetchMyPropertyBookings =
  () =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        //const token = getState().auth.access_token;
        const token = getAuthToken(getState(), "user");

        if (!token) throw new Error("Unauthorized");

        const data = await api<{ data: PropertyBooking[] }>(
          "/user/my-property-bookings",
          "GET",
          token
        );

        dispatch(setMyPropertyBookings(data.data));
      } catch (err: any) {
        dispatch(fail(err));
      }
    };


/**
 * 📌 GET SINGLE BOOKING
 */
export const fetchBooking =
  (id: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getState().auth.access_token;
        if (!token) throw new Error("Unauthorized");

        const data = await api<{ data: PropertyBooking }>(
          `/user/booking/${id}`,
          "GET",
          token
        );

        dispatch(setBooking(data.data));
      } catch (err: any) {
        dispatch(fail(err));
      }
    };

/**
 * 📌 UPDATE BOOKING (STATUS CHANGE)
 */

export const updateBookingStatus =
  (id: string, body: UpdateBookingPayload) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getState().auth.access_token;
        if (!token) throw new Error("Unauthorized");

        const data = await api<{ data: PropertyBooking; message: string }>(
          `/user/booking/${id}/update`,
          "PUT",
          token,
          body
        );

        // ✅ instant UI update
        dispatch(updateBooking(data.data));

        // ✅  FORCE SYNC (THIS IS WHAT YOU WERE MISSING)
        dispatch(fetchBookings());     // admin
        dispatch(fetchMyBookings());  // user

        dispatch(setSuccess(data.message));

        return data;
      } catch (err: any) {
        dispatch(fail(err));
        throw err;
      }
    };

/**
 * 📌 CANCEL BOOKING (USER)
 */
export const cancelBooking =
  (id: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getState().auth.access_token;
        if (!token) throw new Error("Unauthorized");

        await api<{ message: string }>(
          `/user/booking/${id}/cancel`,
          "POST",
          token
        );

        dispatch(deleteBooking(id));
        dispatch(setSuccess("Booking cancelled"));
        //  sync both lists
        dispatch(fetchBookings());
        dispatch(fetchMyBookings());
      } catch (err: any) {
        dispatch(fail(err));
      }
    };

// USER UPDATE OWN BOOKING

export const userUpdateBooking =
  (id: string, body: Partial<CreateBookingPayload>) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAuthToken(getState(), "user");

        if (!token) throw new Error("Unauthorized");

        const data = await api<{
          data: PropertyBooking;
          message: string;
        }>(
          `/user/booking/${id}/user-update`,
          "PUT",
          token,
          body
        );

        dispatch(updateBooking(data.data));

        dispatch(fetchMyBookings());
        dispatch(fetchMyPropertyBookings());

        dispatch(setSuccess(data.message));

        return data;
      } catch (err: any) {
        dispatch(fail(err));
        throw err;
      }
    };

//   PROPERTY OWNER UPDATE BOOKING
export const ownerUpdateBooking =
  (id: string, body: UpdateBookingPayload) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAuthToken(getState(), "user");

        if (!token) throw new Error("Unauthorized");

        const data = await api<{
          data: PropertyBooking;
          message: string;
        }>(
          `/user/booking/${id}/owner-update`,
          "PUT",
          token,
          body
        );

        dispatch(updateBooking(data.data));

        dispatch(fetchMyBookings());
        dispatch(fetchMyPropertyBookings());

        dispatch(setSuccess(data.message));

        return data;
      } catch (err: any) {
        dispatch(fail(err));
        throw err;
      }
    };

//   PROPERTY OWNER CONFIRM BOOKING
export const confirmBooking =
  (id: string, admin_note?: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAuthToken(getState(), "user");

        if (!token) throw new Error("Unauthorized");

        const data = await api<{
          data: PropertyBooking;
          message: string;
        }>(
          `/user/booking/${id}/owner-confirm`,
          "PUT",
          token,
          {
            admin_note,
          }
        );

        dispatch(updateBooking(data.data));

        dispatch(fetchMyBookings());
        dispatch(fetchMyPropertyBookings());

        dispatch(setSuccess(data.message));

        return data;
      } catch (err: any) {
        dispatch(fail(err));
        throw err;
      }
    };


// USER REJECT OWN BOOKING
export const rejectMyBooking =
  (id: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAuthToken(getState(), "user");

        if (!token) throw new Error("Unauthorized");

        const data = await api<{
          data: PropertyBooking;
          message: string;
        }>(
          `/user/booking/${id}/user-reject`,
          "PUT",
          token
        );

        dispatch(updateBooking(data.data));

        dispatch(fetchMyBookings());
        dispatch(fetchMyPropertyBookings());

        dispatch(setSuccess(data.message));

        return data;
      } catch (err: any) {
        dispatch(fail(err));
        throw err;
      }
    };


//   PROPERTY OWNER REJECT BOOKING
export const rejectPropertyBooking =
  (id: string, admin_note?: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAuthToken(getState(), "user");

        if (!token) throw new Error("Unauthorized");

        const data = await api<{
          data: PropertyBooking;
          message: string;
        }>(
          `/user/booking/${id}/owner-reject`,
          "PUT",
          token,
          {
            admin_note,
          }
        );

        dispatch(updateBooking(data.data));

        dispatch(fetchMyBookings());
        dispatch(fetchMyPropertyBookings());

        dispatch(setSuccess(data.message));

        return data;
      } catch (err: any) {
        dispatch(fail(err));
        throw err;
      }
    };


/**
 * 📌 UPDATE BOOKING (ADMIN)
 */
export const updateAdminBooking =
  (id: string, body: UpdateBookingPayload) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAuthToken(getState(), "admin");

        if (!token) throw new Error("Unauthorized (admin)");

        const data = await api<{ data: PropertyBooking; message: string }>(
          `/admin/booking/${id}`,
          "PUT",
          token,
          body
        );

        dispatch(updateBooking(data.data ?? ({ id, ...body } as PropertyBooking)));
        dispatch(setSuccess(data.message || "Booking updated."));

        void dispatch(fetchBookings());

        return data;
      } catch (err: any) {
        dispatch(fail(err));
        throw err;
      }
    };

/**
 * 📌 DELETE BOOKING (ADMIN)
 */
export const deleteBookingAdmin =
  (id: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAuthToken(getState(), "admin");

        if (!token) throw new Error("Unauthorized (admin)");

        await api(
          `/admin/booking/${id}`,
          "DELETE",
          token
        );

        dispatch(deleteBooking(id));
      } catch (err: any) {
        dispatch(fail(err.message));
      }
    };

export type { BookingState };